/**
 * PRP-MCP-5: the estimate page's link to the chat.
 *
 * Loads the chat change log for one estimate, and while the page is visible
 * asks the server for the estimate's version: every 3 seconds for the first
 * 60 seconds after the page opens, after any change, and after the window
 * gets focus; every 10 seconds after 60 quiet seconds (security review M4).
 * A chat change still shows within 5 seconds while the user is working with
 * the chat; after a quiet minute it can take up to 10. When the version
 * moves past the one the page loaded, it re-fetches the estimate (via
 * `reloadEstimate`) and the change log. A refresh never lands while the user is
 * mid-edit (a text field has focus, or a dialog or picker is open): it waits
 * and runs on the first tick after the edit closes.
 *
 * A 404 means the estimate has no chat history (or the server predates the
 * route): it reads as "no chat changes", silently.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { McpEstimateChanges } from '../../lib/mcpApi';
import type { ApiError } from '../../types';
import { buildChatIndex, isEditInProgress } from './chatChanges';
import type { ChatIndex } from './chatChanges';

export const CHAT_POLL_MS = 3000;
export const CHAT_IDLE_POLL_MS = 10000;
export const CHAT_ACTIVE_WINDOW_MS = 60000;

/** Options for {@link useChatChanges}. */
export interface UseChatChangesOptions {
  /** The open estimate's id (its parse hash); undefined disables everything. */
  estimateId: string | undefined;
  /** The `payload_version` of the estimate the page shows. */
  loadedVersion: string | undefined;
  /** Re-fetch the estimate and put it on the page. */
  reloadEstimate: () => Promise<void>;
  /** Override for tests; defaults to {@link isEditInProgress}. */
  editInProgress?: () => boolean;
}

/** What {@link useChatChanges} gives the page. */
export interface ChatChangesState {
  data: McpEstimateChanges | null;
  index: ChatIndex;
  /**
   * Re-load the change log; resolves to the fresh data, or null on a 404 (no
   * chat history). On any other failure it resolves to the old data, or with
   * `strict` it rejects.
   */
  refresh: (_strict?: boolean) => Promise<McpEstimateChanges | null>;
  /** Undo one change; resolves to an error message, or null when it worked. */
  undoChange: (_changeId: string) => Promise<string | null>;
  /** Undo one batch; resolves to an error message, or null when it worked. */
  undoBatch: (_batchId: string) => Promise<string | null>;
}

function isNotFound(err: unknown): boolean {
  return (err as ApiError | undefined)?.status === 404;
}

function messageOf(err: unknown, fallback: string): string {
  const m = (err as ApiError | undefined)?.message;
  return m && m.trim() ? m : fallback;
}

/** Chat change log + version polling for the open estimate. */
export function useChatChanges({
  estimateId,
  loadedVersion,
  reloadEstimate,
  editInProgress = isEditInProgress,
}: UseChatChangesOptions): ChatChangesState {
  const [data, setData] = useState<McpEstimateChanges | null>(null);
  const dataRef = useRef<McpEstimateChanges | null>(null);
  dataRef.current = data;

  const loadedVersionRef = useRef(loadedVersion);
  loadedVersionRef.current = loadedVersion;
  const reloadRef = useRef(reloadEstimate);
  reloadRef.current = reloadEstimate;
  const editRef = useRef(editInProgress);
  editRef.current = editInProgress;

  // When the page last saw activity (open, a change, focus): drives the poll interval.
  const activityRef = useRef(Date.now());

  const refresh = useCallback(
    async (strict = false): Promise<McpEstimateChanges | null> => {
      if (!estimateId) return null;
      try {
        const res = await McpAPI.getChanges(estimateId);
        setData(res);
        return res;
      } catch (err) {
        if (isNotFound(err)) {
          setData(null);
          return null;
        }
        if (strict) throw err;
        return dataRef.current;
      }
    },
    [estimateId],
  );

  // Load the log when the estimate changes.
  useEffect(() => {
    setData(null);
    if (estimateId) void refresh();
  }, [estimateId, refresh]);

  // Follow the chat: poll the version while the page is visible.
  useEffect(() => {
    if (!estimateId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    // Set when the server is ahead and the refresh is waiting for an edit to close.
    let pending: { estimate: boolean } | null = null;

    const runRefresh = async (estimate: boolean) => {
      if (estimate) await reloadRef.current();
      await refresh();
    };

    const tick = async () => {
      if (stopped || inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        if (!pending) {
          try {
            const v = await McpAPI.getVersion(estimateId);
            if (stopped) return;
            const known = dataRef.current;
            const estimateMoved = !!v.version && v.version !== loadedVersionRef.current;
            const countsMoved = (known?.parked.length ?? 0) !== (v.parked_count ?? 0);
            if (estimateMoved || countsMoved) {
              pending = { estimate: estimateMoved };
              activityRef.current = Date.now();
            }
          } catch {
            return; // 404 or a blip: nothing to follow this tick
          }
        }
        if (pending && !editRef.current()) {
          const p = pending;
          pending = null;
          await runRefresh(p.estimate);
        }
      } finally {
        inFlight = false;
      }
    };

    const delay = () =>
      Date.now() - activityRef.current < CHAT_ACTIVE_WINDOW_MS ? CHAT_POLL_MS : CHAT_IDLE_POLL_MS;

    const schedule = () => {
      if (stopped) return;
      timer = setTimeout(() => {
        timer = null;
        void tick().finally(schedule);
      }, delay());
    };

    // Focus (or the tab coming back) counts as activity: poll now, then every 3 s.
    const wake = () => {
      activityRef.current = Date.now();
      if (timer === null) return; // a tick is running; it schedules the next at 3 s
      clearTimeout(timer);
      timer = null;
      void tick().finally(schedule);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake();
    };

    activityRef.current = Date.now();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', wake);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', wake);
    };
  }, [estimateId, refresh]);

  const afterUndo = useCallback(async () => {
    activityRef.current = Date.now();
    await reloadRef.current();
    await refresh();
  }, [refresh]);

  const undoChange = useCallback(
    async (changeId: string): Promise<string | null> => {
      try {
        await McpAPI.undoChange(changeId);
      } catch (err) {
        return messageOf(err, 'That change could not be undone.');
      }
      await afterUndo();
      return null;
    },
    [afterUndo],
  );

  const undoBatch = useCallback(
    async (batchId: string): Promise<string | null> => {
      try {
        await McpAPI.undoBatch(batchId);
      } catch (err) {
        return messageOf(err, 'Those changes could not be undone.');
      }
      await afterUndo();
      return null;
    },
    [afterUndo],
  );

  const index = useMemo(() => buildChatIndex(data?.changes), [data]);

  return { data, index, refresh, undoChange, undoBatch };
}
